import { useState, useRef } from "react";
import { motion } from "framer-motion";
import {
  Search, MessageSquare, Phone, Send, FileText, Calendar, Share2,
  User, Building2, DollarSign, Zap, Clock, ChevronRight, Mic
} from "lucide-react";
import { CONVERSATIONS, LEADS } from "@/data/dummyData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QUICK_ACTIONS = [
  { icon: Calendar, label: "Schedule Visit", color: "text-orange-600 bg-orange-50" },
  { icon: FileText, label: "Share Brochure", color: "text-blue-600 bg-blue-50" },
  { icon: Share2, label: "Share Floor Plan", color: "text-purple-600 bg-purple-50" },
  { icon: User, label: "Assign Agent", color: "text-green-600 bg-green-50" },
  { icon: Phone, label: "Call Customer", color: "text-rose-600 bg-rose-50" },
];

const stageColors = {
  new: "bg-slate-100 text-slate-700", whatsapp_connected: "bg-green-100 text-green-700",
  ai_called: "bg-purple-100 text-purple-700", qualified: "bg-blue-100 text-blue-700",
  site_visit: "bg-orange-100 text-orange-700", negotiation: "bg-yellow-100 text-yellow-700",
  booked: "bg-emerald-100 text-emerald-700",
};

export default function Conversations() {
  const [selected, setSelected] = useState(CONVERSATIONS[0]);
  const [search, setSearch] = useState("");
  const [messageInput, setMessageInput] = useState("");
  const [aiSummary, setAiSummary] = useState("");
  const [summaryLoading, setSummaryLoading] = useState(false);
  const inputRef = useRef(null);

  const lead = LEADS.find(l => l.id === selected?.leadId);

  const filtered = CONVERSATIONS.filter(c =>
    c.leadName.toLowerCase().includes(search.toLowerCase()) ||
    c.propertyInterest.toLowerCase().includes(search.toLowerCase())
  );

  const getSummary = async () => {
    setSummaryLoading(true);
    setAiSummary("");
    try {
      const messages = selected.channel === "whatsapp" ? selected.messages : selected.transcript?.map(t => ({ from: t.speaker, text: t.text })) || [];
      const res = await fetch(`${API}/ai/conversation-summary`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lead_name: selected.leadName, messages, channel: selected.channel }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value);
        for (const line of chunk.split("\n")) {
          if (line.startsWith("data: ")) {
            const text = line.slice(6);
            if (text === "[DONE]") { setSummaryLoading(false); return; }
            setAiSummary(p => p + text);
          }
        }
      }
    } catch {
      setAiSummary("Error generating summary.");
    } finally {
      setSummaryLoading(false);
    }
  };

  return (
    <div className="flex h-[calc(100vh-64px)] overflow-hidden" data-testid="conversations-page">
      {/* Left Panel - Conversation List */}
      <div className="w-72 shrink-0 border-r border-slate-200 bg-white flex flex-col">
        <div className="p-4 border-b border-slate-100">
          <h3 className="font-heading font-semibold text-slate-900 mb-3">Conversations</h3>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-300"
              data-testid="conversation-search"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {filtered.map(conv => (
            <div
              key={conv.id}
              className={`p-3.5 border-b border-slate-50 cursor-pointer transition-colors hover:bg-slate-50 ${selected?.id === conv.id ? "bg-blue-50 border-l-2 border-l-blue-500" : ""}`}
              onClick={() => { setSelected(conv); setAiSummary(""); }}
              data-testid={`conv-item-${conv.id}`}
            >
              <div className="flex items-start gap-2.5">
                <div className="relative shrink-0">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-400 to-indigo-600 flex items-center justify-center text-white text-xs font-bold">
                    {conv.avatar}
                  </div>
                  {conv.channel === "whatsapp" ? (
                    <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                      <MessageSquare className="w-2 h-2 text-white" />
                    </div>
                  ) : (
                    <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-purple-500 rounded-full flex items-center justify-center">
                      <Phone className="w-2 h-2 text-white" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-slate-800 truncate">{conv.leadName}</p>
                    <span className="text-xs text-slate-400 shrink-0 ml-2">{conv.time}</span>
                  </div>
                  <p className="text-xs text-slate-500 truncate mt-0.5">{conv.lastMessage}</p>
                </div>
              </div>
              {conv.unread > 0 && (
                <div className="flex justify-end mt-1">
                  <span className="w-4 h-4 bg-blue-600 text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {conv.unread}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Center Panel - Chat / Transcript */}
      {selected && (
        <div className="flex-1 flex flex-col bg-slate-50 min-w-0">
          {/* Chat Header */}
          <div className="h-14 bg-white border-b border-slate-200 flex items-center gap-3 px-5 shrink-0">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-indigo-600 flex items-center justify-center text-white text-xs font-bold">
              {selected.avatar}
            </div>
            <div>
              <p className="font-semibold text-slate-900 text-sm">{selected.leadName}</p>
              <p className="text-xs text-slate-400">{selected.propertyInterest}</p>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${selected.channel === "whatsapp" ? "bg-green-50 text-green-700" : "bg-purple-50 text-purple-700"}`}>
                {selected.channel === "whatsapp" ? "WhatsApp" : "AI Call Transcript"}
              </span>
              <button
                onClick={getSummary}
                className="flex items-center gap-1.5 text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 px-3 py-1.5 rounded-lg font-semibold transition-colors"
                data-testid="ai-summary-btn"
              >
                <Zap className="w-3 h-3" /> AI Summary
              </button>
            </div>
          </div>

          {/* AI Summary Bar */}
          {(aiSummary || summaryLoading) && (
            <div className="mx-4 mt-3 bg-blue-50 border border-blue-200 rounded-xl p-3">
              <div className="flex items-center gap-2 mb-1.5">
                <Zap className="w-3.5 h-3.5 text-blue-600" />
                <span className="text-xs font-semibold text-blue-700">AI Conversation Summary</span>
              </div>
              {summaryLoading && !aiSummary ? (
                <div className="flex items-center gap-2 text-blue-500 text-xs">
                  <div className="w-3 h-3 border border-blue-500 border-t-transparent rounded-full animate-spin" />
                  Analyzing conversation...
                </div>
              ) : (
                <p className="text-xs text-blue-800 leading-relaxed ai-stream">{aiSummary}</p>
              )}
            </div>
          )}

          {/* Messages / Transcript */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="messages-container">
            {selected.channel === "whatsapp" && selected.messages?.map(msg => (
              <div key={msg.id} className={`flex ${msg.from === "lead" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-xs lg:max-w-md px-4 py-2.5 rounded-2xl text-sm ${
                  msg.from === "lead"
                    ? "bg-blue-600 text-white rounded-br-sm"
                    : "bg-white text-slate-800 border border-slate-200 rounded-bl-sm shadow-sm"
                }`}>
                  {msg.isFile ? (
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 shrink-0" />
                      <span className="text-xs">{msg.text}</span>
                    </div>
                  ) : msg.text}
                  <p className={`text-xs mt-1 opacity-60 text-right`}>{msg.time}</p>
                </div>
              </div>
            ))}

            {selected.channel === "ai_call" && selected.transcript?.map((line, i) => (
              <div key={i} className={`flex items-start gap-2.5 ${line.speaker === "AI" ? "justify-start" : "justify-end flex-row-reverse"}`}>
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${line.speaker === "AI" ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700"}`}>
                  {line.speaker === "AI" ? <Mic className="w-3 h-3" /> : lead?.avatar?.charAt(0) || "L"}
                </div>
                <div className={`max-w-xs lg:max-w-md px-3 py-2 rounded-xl text-sm ${line.speaker === "AI" ? "bg-white border border-slate-200 text-slate-700 shadow-sm" : "bg-purple-600 text-white"}`}>
                  <p className={`text-xs font-semibold mb-1 ${line.speaker === "AI" ? "text-purple-600" : "text-purple-200"}`}>{line.speaker}</p>
                  {line.text}
                  <p className="text-xs opacity-50 mt-1">{line.time}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Input */}
          {selected.channel === "whatsapp" && (
            <div className="p-4 bg-white border-t border-slate-200">
              <div className="flex items-center gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={messageInput}
                  onChange={e => setMessageInput(e.target.value)}
                  placeholder="Type a message..."
                  className="flex-1 border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-300"
                  data-testid="message-input"
                />
                <button
                  className="w-10 h-10 bg-blue-600 hover:bg-blue-700 text-white rounded-xl flex items-center justify-center transition-colors shrink-0"
                  onClick={() => setMessageInput("")}
                  data-testid="send-message-btn"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Right Panel - Lead Details */}
      {selected && lead && (
        <div className="w-72 shrink-0 border-l border-slate-200 bg-white flex flex-col overflow-y-auto">
          <div className="p-4 border-b border-slate-100">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Lead Details</p>
            <div className="flex items-center gap-2.5 mb-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-indigo-600 flex items-center justify-center text-white text-sm font-bold">
                {lead.avatar}
              </div>
              <div>
                <p className="font-semibold text-slate-900 text-sm">{lead.name}</p>
                <p className="text-xs text-slate-400">{lead.phone}</p>
              </div>
            </div>
            <span className={`text-xs px-2 py-1 rounded-full font-semibold ${stageColors[lead.stage]}`}>
              {lead.stage.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
            </span>
          </div>

          {/* Lead Info */}
          <div className="p-4 border-b border-slate-100 space-y-3">
            {[
              { icon: Building2, label: "Property", value: lead.propertyInterest },
              { icon: DollarSign, label: "Budget", value: lead.budget },
              { icon: User, label: "Config", value: lead.config },
              { icon: Clock, label: "In Pipeline", value: `${lead.daysInPipeline} days` },
            ].map(item => (
              <div key={item.label} className="flex items-center gap-2">
                <div className="w-7 h-7 bg-slate-50 rounded-lg flex items-center justify-center shrink-0">
                  <item.icon className="w-3.5 h-3.5 text-slate-500" />
                </div>
                <div>
                  <p className="text-xs text-slate-400">{item.label}</p>
                  <p className="text-xs font-semibold text-slate-800">{item.value}</p>
                </div>
              </div>
            ))}
          </div>

          {/* AI Score */}
          <div className="p-4 border-b border-slate-100">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-slate-700">AI Lead Score</p>
              <span className="text-lg font-heading font-bold text-blue-600">{lead.score}</span>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-2">
              <div className={`h-2 rounded-full transition-all ${lead.score >= 80 ? "bg-emerald-500" : lead.score >= 60 ? "bg-blue-500" : "bg-yellow-500"}`} style={{ width: `${lead.score}%` }} />
            </div>
          </div>

          {/* Notes */}
          {lead.notes && (
            <div className="p-4 border-b border-slate-100">
              <p className="text-xs font-semibold text-slate-500 mb-1">Notes</p>
              <p className="text-xs text-slate-700 leading-relaxed">{lead.notes}</p>
            </div>
          )}

          {/* Quick Actions */}
          <div className="p-4">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">AI Quick Actions</p>
            <div className="space-y-2">
              {QUICK_ACTIONS.map(action => (
                <button
                  key={action.label}
                  className={`w-full flex items-center gap-2.5 p-2.5 rounded-lg text-xs font-semibold transition-colors hover:opacity-80 ${action.color} border border-transparent hover:border-current/20`}
                  data-testid={`quick-action-${action.label.toLowerCase().replace(/\s/g, "-")}`}
                >
                  <action.icon className="w-3.5 h-3.5 shrink-0" />
                  {action.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
